<template>

    <div class="bg-white my-8 mx-6">
        <div class="pb-2 px-8 pt-12">
            <ErrorMessage :message="errorMessage" @clear-message="errorMessage = ''" />
            <div class="flex flex-col gap-y-2 sm:flex-row justify-between">
            <div>
                <h1 class="text-2xl font-bold">メンバー管理</h1>
                <p class="mt-3 text-gray-700 text-sm">ECHOESに登録済みのメールアドレスのみ招待できます</p>
                </div>
                <!-- メンバーを追加ボタンを右上、SP中央に配置 -->
                <div class="flex flex-col gap-2 items-center w-full sm:items-start sm:w-auto">
                    <button
                        class="btn-primary py-3 w-full sm:w-auto"
                        @click="isVisibleAdditionalModal = true"
                    >
                        メンバーを追加
                    </button>
                </div>
            </div>
        </div>

        <div class="mt-6">
            <div class="overflow-x-auto px-8 pb-12">
                <table class="w-full table-fixed min-w-[720px]">
                    <thead class="text-sm border-b whitespace-nowrap">
                        <tr class="[&>th]:py-3 [&>th]:px-2 text-sub">
                            <th class="w-[25%]">表示名</th>
                            <th class="w-[40%]">メールアドレス</th>
                            <th class="w-[25%]">権限</th>
                            <th class="w-[10%]">削除</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="member in members"
                            :key="member.id"
                            class="[&>td]:py-4 [&>td]:px-3 bg-white border-b"
                        >
                            <!-- 表示名 -->
                            <td>
                                <div class="flex flex-wrap md:flex-row gap-2 items-center md:items-center justify-start min-w-0">
                                    <div
                                        class="shrink-0 w-8 h-8 rounded-full overflow-hidden"
                                    >
                                        <img
                                            v-if="member.image_url"
                                            :src="member.image_url"
                                            alt="Avatar"
                                            class="w-full h-full object-cover"
                                        />

                                        <div
                                            v-else
                                            class="w-full h-full object-cover bg-gray-100 flex justify-center items-center shrink-0 mx-auto border border-gray-200 border-solid border-[1px]">
                                            <i class="fa-solid fa-user text-xl text-gray-500" />
                                        </div>
                                    </div>
                                    <div class="flex-1 min-w-0">
                                        <span class="block truncate whitespace-nowrap overflow-hidden" :title="member.name">
                                            {{ member.name }}
                                        </span>
                                    </div>
                                </div>
                            </td>

                            <!-- メールアドレス -->
                            <td class="text-left text-sm min-w-0">
                                <span class="block truncate whitespace-nowrap overflow-hidden" :title="member.email">
                                    {{ member.email }}
                                </span>
                            </td>

                            <!-- 権限 or ステータス -->
                            <td class="whitespace-nowrap text-center text-sm">
                                <span :class="statusOrRoleClass(member)">
                                    {{ statusOrRoleLabel(member) }}
                                </span>
                            </td>

                            <!-- 削除 -->
                            <td class="whitespace-nowrap text-center text-sm">
                                <button
                                    v-if="!member.is_owner"
                                    @click="selectedMember = member"
                                    class="text-gray-400 hover:underline"
                                >
                                    削除
                                </button>
                                <span v-else>&nbsp;</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>


        <!-- 追加用のモーダル -->
        <div
            v-if="isVisibleAdditionalModal"
            class="fixed inset-0 bg-modal flex items-center justify-center z-50 p-4"
        >
            <MemberAdditionalModal
                :showModal="isVisibleAdditionalModal"
                @close-modal="isVisibleAdditionalModal = false"
                @load-member="loadMembers()"
            />
        </div>

        <!-- 削除用のモーダル -->
        <div
            v-if="selectedMember"
            class="fixed inset-0 bg-modal flex items-center justify-center z-50 p-4"
        >
            <MemberDeletionModal
                :email="selectedMember['email']"
                :name="selectedMember['name']"
                :id="selectedMember['id']"
                :image_url="selectedMember['image_url']"
                @close-modal="selectedMember = null"
                @load-member="loadMembers()"
            />
        </div>
    </div>
</template>




<script>
import { ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';
import MemberAdditionalModal from './MemberAdditionalModal.vue';
import MemberDeletionModal from './MemberDeletionModal.vue';
import ErrorMessage from './ErrorMessage.vue';

export default {
    components: {
        MemberAdditionalModal,
        MemberDeletionModal,
        ErrorMessage,
    },

    setup() {
        const store = useStore();

        const members = ref([]);
        const isVisibleAdditionalModal = ref(false);
        const selectedMember = ref(null);
        const errorMessage = ref('');

        const loadMembers = async () => {
            const accessToken = store.state.accessToken;
            const apiEndpoint = process.env.VUE_APP_API_ENDPOINT;
            try {
                const response = await axios.get(`${apiEndpoint}/member-invitations/`, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`
                    }
                });
                if (response.status === 200) {
                    members.value = response.data.results;
                }
            } catch (error) {
                console.error('メンバー情報の取得に失敗しました。', error);
                if (error.response?.status === 403) {
                    errorMessage.value = "メンバー情報へのアクセス権限がありません";
                }
            }
        };

        const statusOrRoleClass = (member) => {
            // 承認済みの場合は普通の色
            if (member.status === 1) {
                return 'text-gray-500';
            } else {
                if (member.status === 0) {
                    return 'text-green-800';
                } else {
                    return 'text-red-800';
                }
            }
        };

        const statusOrRoleLabel = (member) => {
            // 承認済みの場合は権限を表示
            if (member.status === 1) {
                switch (member.role) {
                    case 1:
                        return '管理者';
                    case 2:
                        return '編集者';
                }
            } else {
                // 承認済み以外はステータスを表示
                switch (member.status) {
                    case 0:
                        return '招待中';
                    case 2:
                        return '辞退';
                    case 3:
                        return '招待失敗';
                    case 4:
                        return 'メールアドレス不明';
                    default:
                        return '不明';
                }
            }
        };

        onMounted(loadMembers);

        return {
            members,
            isVisibleAdditionalModal,
            selectedMember,
            loadMembers,
            statusOrRoleClass,
            statusOrRoleLabel,
            errorMessage,
        };
    }
};
</script>
